// src/components/ProductsPage/ProductDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./ProductDetail.scss";
import PageBanner from "../layouts/PageBanner";
import aboutProducts from "../../assets/about-hero.jpg";

const API = "https://api.ainfotech.com.np";

const ProductDetail = () => {
  const { group, key } = useParams(); // group = category slug; key = product slug
  const navigate = useNavigate();

  const [item, setItem] = useState(null);
  const [all, setAll] = useState([]);

  // Load product detail by slug (key)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/products/${key}`);
        if (res.status === 404) {
          setItem(null);
          return;
        }
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "Failed to load product");
        setItem(data);
        // console.log("PRODUCT", data);
        window.scrollTo(0, 0);
      } catch (e) {
        console.error(e);
        setItem(null);
      }
    })();
  }, [key]);

  // Load all products for sidebar
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/products?status=published&page=1&limit=200`);
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "Failed to load products");
        setAll(Array.isArray(data.items) ? data.items : []);
      } catch (e) {
        console.error(e);
        setAll([]);
      }
    })();
  }, []);

  // Sidebar list is simply all published products
  const allItems = useMemo(() => all, [all]);

  // Resolve Jodit HTML content and fix relative URLs (src/href/url())
  const resolvedHtml = useMemo(() => {
    if (!item) return "";
    const raw =
      item?.content_html ??
      item?.contentHtml ??
      item?.content ??
      item?.description_html ??
      "";

    // Prefix relative src/href and CSS url(/...) with API base
    return raw
      .replace(/(\s(src|href)=["'])\/(?!\/)/g, `$1${API}/`)
      .replace(/url\(["']?\/(?!\/)/g, `url(${API}/`);
  }, [item]);

  if (!item) {
    return (
      <div className="container py-5">
        <h3 className="mb-2">Product not found</h3>
        <p className="text-muted">
          The product you’re looking for may have been moved or renamed.
        </p>
        <Link to="/productpage" className="btn btn-outline-secondary">
          Back to Products
        </Link>
      </div>
    );
  }

  const bannerTitle = item.title || "Product Details";
  const hero = item.cover_image ? `${API}${item.cover_image}` : aboutProducts;

  return (
    <div className="avena-product-detail">
      <PageBanner
        title={bannerTitle}
        bg={aboutProducts}
        crumbs={[
          { label: "Home", href: "/" },
          { label: group },
          { label: item.title },
        ]}
      />

      <section className="pd-body">
        <div className="container">
          <div className="row g-4">
            {/* LEFT: Title & Content */}
            <div className="col-12 col-lg-8">
              <article className="pd-article">
                <header className="pd-header">
                   <h1 className="pd-title">{item.title}</h1>
                  {group && <span className="pd-chip">{group}</span>}
                 
                </header>

                <div
                  className="pd-hero"
                  style={{ backgroundImage: `url(${hero})` }}
                  role="img"
                  aria-label={item.title}
                />

                <div className="pd-content">
                  {/* {item.excerpt && <p className="pd-desc">{item.excerpt}</p>} */}

                  {/* Jodit HTML from backend */}
                  <div
                    className="pd-rich"
                    dangerouslySetInnerHTML={{ __html: resolvedHtml }}
                  />

                 
                </div>
              </article>
            </div>

            {/* RIGHT: Sidebar (ALL items) */}
            <div className="col-12 col-lg-4">
              <aside className="pd-sidebar">
                <div className="pd-sidecard">
                  <h5 className="pd-sidecard__title">All Products</h5>
                  <ul className="pd-side-list">
                    {allItems.map((o) => {
                      const thumb = o.cover_image ? `${API}${o.cover_image}` : aboutProducts;
                      return (
                        <li key={o.id}>
                          <button
                            type="button"
                            className={`pd-side-item ${o.slug === key ? "is-active" : ""}`}
                            onClick={() =>
                              navigate(
                                `/products/${group || (o.ProductCategory?.slug || "all")}/${o.slug}`
                              )
                            }
                          >
                            <div
                              className="pd-side-thumb"
                              style={{ backgroundImage: `url(${thumb})` }}
                            />
                            <span className="pd-side-name">
                              {o.title}
                              <small className="d-block text-muted" style={{ fontSize: "0.75rem" }}>
                                {o.ProductCategory?.name || ""}
                              </small>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="pd-cta">
                  <div
                    className="pd-cta__bg"
                    style={{ backgroundImage: `url(${hero})` }}
                    aria-hidden="true"
                  />
                  <div className="pd-cta__content">
                    <h5 className="pd-cta__title">
                      Ready for your next
                      <br />
                      deployment?
                    </h5>
                    <Link to="/contact" className="pd-cta__btn">
                      Get an estimate
                      <span className="pd-cta__btnIcon" aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M5 12h14M13 5l7 7-7 7"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </Link>
                  </div>
                </div>

                <Link
                  to={`/productpage?group=${encodeURIComponent(group || "")}`}
                  className="pd-backlink"
                >
                  ← Back to Products
                </Link>
              </aside>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
